// src/oura/publish/PublishRefusalNotice.tsx
//
// What a publish screen shows when the server refuses a publish (H20, H20a;
// scope 2.23). It renders only from a refusal the server sent, so it can go
// up with Delivery's gate on its own.
//
//   Needs the client's yes   The server's sentence and the key it waits on,
//                            naming the doc that still needs their yes, with
//                            "Open the doc".
//   Record the client's yes  For a client's yes given somewhere else (a call,
//                            WhatsApp): a named person records where they
//                            said yes and why. It holds for every later change
//                            on that key, until someone removes it, and shows
//                            in the doc's approval record. A Phase 2 write:
//                            offered only with the Docs write switch on.
//                            Publishing stays that person's own click.
//   Not on the plan          The server's sentence; nothing to override.
//
// Amber: the move is the client's; emerald once their yes is recorded. Staff
// screens only.

import { useEffect, useId, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FileTextIcon, SendIcon } from "lucide-react";
import type { ApprovalOverrideRecord, PublishRefusal } from "@service/aeo/deliveryTypes";
import { recordClientYes } from "@service/aeo/publishRefusal";
import { LATE_TEXT, PillButton, PillLink, TYPE } from "../ui";
import { cn } from "../lib/cn";
import { OURA_ROOT } from "../nav";
import { useSignedInName } from "../docs/signedIn";
import { DOCS_WRITE_ON } from "../docs/writeFlag";

const FIELD = cn(
    TYPE.tb,
    "block w-full rounded-xl border-0 bg-white px-3 py-2 text-gray-900 ring-1 ring-inset ring-black/[0.06] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600",
);

const HEAD: Record<PublishRefusal["code"], string> = {
    APPROVAL_REQUIRED: "Needs the client's yes",
    draft_not_on_the_plan: "Not on the plan",
    wrong_item_for_draft: "Not this item's draft",
};

/** A typed reason, ending as a sentence. */
const asSentence = (t: string) => (/[.!?]$/.test(t.trim()) ? t.trim() : `${t.trim()}.`);

/** "B1:page_copy" as "B1 page copy": the key a refusal waits on. */
function keyText(key: string): string {
    return key.replace(":", " ").replace(/_/g, " ");
}

export interface PublishRefusalNoticeProps {
    refusal: PublishRefusal;
    /** The business; from the route when absent. */
    businessId?: string;
    /** Publish again, after the client's yes is recorded. Absent: the screen's own Publish does it. */
    onRetry?: () => void;
    retrying?: boolean;
    className?: string;
}

export function PublishRefusalNotice({ refusal, businessId: given, onRetry, retrying = false, className }: PublishRefusalNoticeProps) {
    const params = useParams<{ businessId?: string }>();
    const businessId = given ?? params.businessId ?? null;
    const name = useSignedInName();
    const ids = useId();
    const [open, setOpen] = useState(false);
    const [where, setWhere] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [recorded, setRecorded] = useState<ApprovalOverrideRecord | null>(null);

    // A different refusal starts the notice afresh: a yes recorded for the last
    // key must never show green over a new refusal, nor a half-typed form carry
    // over to another key or doc. Read from the refusal's own fields, so a parent
    // that hands in a fresh object for the same refusal does not wipe the form.
    const refusalIdentity = [refusal.code, refusal.approvalKey ?? "", refusal.docId ?? "", refusal.message].join("\n");
    const identityRef = useRef(refusalIdentity);
    useEffect(() => {
        identityRef.current = refusalIdentity;
        setOpen(false);
        setWhere("");
        setReason("");
        setSaving(false);
        setError(null);
        setRecorded(null);
    }, [refusalIdentity]);

    const docHref = businessId && refusal.docId ? `${OURA_ROOT}/businesses/${encodeURIComponent(businessId)}/reviews?doc=${encodeURIComponent(refusal.docId)}` : null;
    const canRecord = DOCS_WRITE_ON && refusal.canOverride && Boolean(refusal.approvalKey) && Boolean(businessId);
    const key = refusal.approvalKey ? keyText(refusal.approvalKey) : null;

    const save = async () => {
        if (!businessId || !refusal.approvalKey || saving) return;
        setSaving(true);
        setError(null);
        const savingFor = refusalIdentity;
        // A save that throws reads as one that did not save, so the form never stays greyed out.
        const r = await recordClientYes(businessId, refusal.approvalKey, { requirement: "not_required", showToClient: true, name: name ?? "", reason, whereClientSaidYes: where }).catch(() => null);
        // The refusal changed while this saved: the answer is about the old one.
        if (identityRef.current !== savingFor) return;
        setSaving(false);
        if (!r) {
            setError("That did not save. Try again.");
            return;
        }
        if (!r.ok) {
            setError(r.message);
            return;
        }
        setRecorded(r.data);
        setOpen(false);
    };

    return (
        <div role="alert" className={cn("rounded-2xl p-4", recorded ? "bg-emerald-50" : "bg-amber-50", className)}>
            <p className={cn(TYPE.tb, "m-0 flex flex-wrap items-center gap-x-2 font-medium", recorded ? "text-emerald-900" : "text-amber-900")}>
                <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full", recorded ? "bg-emerald-500" : "bg-amber-500")} />
                {recorded ? "The client's yes is recorded" : HEAD[refusal.code]}
                {key && <span className="font-normal opacity-80">· {key}</span>}
            </p>
            {recorded ? (
                <p className={cn(TYPE.tc, "m-0 mt-1 text-emerald-900")}>
                    Recorded by {recorded.setBy}: {recorded.whereClientSaidYes}. {asSentence(recorded.reason)} Every later {key ? `${key} change` : "change"} on this business publishes without asking the client again, until someone removes it. It shows in the doc&rsquo;s approval record.
                </p>
            ) : (
                <p className={cn(TYPE.tc, "m-0 mt-1 text-amber-900")}>{refusal.message}</p>
            )}

            {!recorded && !open && (docHref || canRecord) && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    {docHref && (
                        <PillLink to={docHref} size="sm" variant="secondary" icon={FileTextIcon} aria-label={`Open the doc: ${refusal.docTitle ?? "the client document"}`}>
                            Open the doc
                        </PillLink>
                    )}
                    {canRecord && (
                        <PillButton size="sm" variant="text" onClick={() => setOpen(true)}>
                            Record the client&rsquo;s yes
                        </PillButton>
                    )}
                </div>
            )}

            {open && !recorded && (
                <div className="mt-3 space-y-3">
                    <p className={cn(TYPE.tc, "m-0 text-amber-900")}>
                        For a yes the client gave outside the doc. Once recorded, every later {key ? `${key} change` : "change"} on this business publishes without asking the client again, until someone removes it.
                    </p>
                    <div>
                        <label htmlFor={`${ids}-where`} className={cn(TYPE.lab, "block text-amber-900")}>
                            Where the client said yes
                        </label>
                        <input
                            id={`${ids}-where`}
                            value={where}
                            onChange={(e) => setWhere(e.target.value)}
                            placeholder="WhatsApp, 1 Oct, 3.12pm"
                            className={cn(FIELD, "mt-1 h-10")}
                        />
                    </div>
                    <div>
                        <label htmlFor={`${ids}-reason`} className={cn(TYPE.lab, "block text-amber-900")}>
                            Reason
                        </label>
                        <textarea
                            id={`${ids}-reason`}
                            rows={2}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Siew Ling agreed the wording on a call with Angel"
                            className={cn(FIELD, "mt-1 resize-y")}
                        />
                    </div>
                    <p className={cn(TYPE.tc, "m-0 text-amber-900")}>{name ? `Recorded as ${name}.` : "Your profile has no name yet. Add it to record this."}</p>
                    {error && (
                        <p role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0")}>
                            {error}
                        </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                        <PillButton variant="primary" size="sm" busy={saving} disabled={!name || !where.trim() || !reason.trim()} onClick={() => void save()}>
                            Record it
                        </PillButton>
                        <PillButton size="sm" disabled={saving} onClick={() => setOpen(false)}>
                            Cancel
                        </PillButton>
                    </div>
                </div>
            )}

            {recorded && onRetry && (
                <div className="mt-3">
                    <PillButton size="sm" icon={SendIcon} busy={retrying} onClick={onRetry}>
                        Publish again
                    </PillButton>
                </div>
            )}
        </div>
    );
}
